import { useEffect, useMemo, useState } from "react";
import { ClipboardList, User } from "lucide-react";

import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { EMPLOYEE_AUDIT } from "../../../constants/api";
import { checkUserPermissionFunc } from "../../../components/common/CommonFunctions";
import { cn } from "../../../utils/tailwindUtils";
import { SelectField } from "../../../components/ui/form-fields";

const CATEGORY_OPTIONS = [
    { value: "all", label: "All categories" },
    { value: "profile", label: "Profile" },
    { value: "attendance", label: "Attendance" },
    { value: "leave", label: "Leave / permission" },
    { value: "resignation", label: "Resignation" },
    { value: "document", label: "Documents" },
    { value: "branch_transfer", label: "Branch transfer" },
    { value: "payroll_hold", label: "Payroll hold" },
    { value: "status", label: "Status" },
];

const CATEGORY_CHIP = {
    profile: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    attendance: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    leave: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
    resignation: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    document: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    branch_transfer: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
    payroll_hold: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    status: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
};

const EmployeeAuditContent = ({ employeeID, embedded = false }) => {
    const { fetch } = useApiMethods();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [category, setCategory] = useState("all");

    const isOrgAdmin =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
    const employeesPerm = checkUserPermissionFunc("employee", "employees", "SubMenu");
    const canViewAudit = embedded || isOrgAdmin || employeesPerm.edit;

    useEffect(() => {
        if (!employeeID || !canViewAudit) return;

        const loadAudit = async () => {
            setLoading(true);
            try {
                const filters = { employee_id: employeeID };
                if (category !== "all") filters.category = category;
                const apiData = await fetch(EMPLOYEE_AUDIT, filters);
                if (apiData?.status === "success") {
                    setRows(apiData.data || []);
                } else {
                    ToastMsg("error", apiData?.message || "Failed to load audit history");
                    setRows([]);
                }
            } catch (error) {
                ToastMsg("error", error.message);
                setRows([]);
            } finally {
                setLoading(false);
            }
        };

        loadAudit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID, category, canViewAudit]);

    const groupedCount = useMemo(() => {
        return rows.reduce((acc, row) => {
            const key = row.audit_category || "profile";
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
    }, [rows]);

    if (!canViewAudit) {
        return (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
                <ClipboardList className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                <p className="text-sm text-muted-foreground">
                    Audit history is available to HR administrators only.
                </p>
            </div>
        );
    }

    if (loading && rows.length === 0) {
        return (
            <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <ClipboardList className="h-5 w-5 text-sidebar-primary" />
                        Audit Trail
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Field-level change history with previous and updated values for compliance review.
                    </p>
                </div>
                <SelectField
                    size="sm"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-auto min-w-[12rem]"
                >
                    {CATEGORY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </SelectField>
            </div>

            {Object.keys(groupedCount).length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {Object.entries(groupedCount).map(([key, count]) => (
                        <span key={key} className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800">
                            {(CATEGORY_OPTIONS.find((o) => o.value === key)?.label || key)}: {count}
                        </span>
                    ))}
                </div>
            )}

            {rows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
                    <ClipboardList className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm text-muted-foreground">No audit records found for this employee.</p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-900/50 text-left">
                            <tr>
                                <th className="px-3 py-2 font-medium text-muted-foreground whitespace-nowrap">Date &amp; Time</th>
                                <th className="px-3 py-2 font-medium text-muted-foreground whitespace-nowrap">Category</th>
                                <th className="px-3 py-2 font-medium text-muted-foreground whitespace-nowrap">Field</th>
                                <th className="px-3 py-2 font-medium text-muted-foreground whitespace-nowrap">Previous Value</th>
                                <th className="px-3 py-2 font-medium text-muted-foreground whitespace-nowrap">Updated Value</th>
                                <th className="px-3 py-2 font-medium text-muted-foreground whitespace-nowrap">Changed By</th>
                                <th className="px-3 py-2 font-medium text-muted-foreground whitespace-nowrap">Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const chip = CATEGORY_CHIP[row.audit_category] || CATEGORY_CHIP.profile;
                                const notes = [row.reason, row.source].filter(Boolean).join(" · ");
                                return (
                                    <tr
                                        key={row.id}
                                        className="border-t border-gray-200 dark:border-gray-700 align-top hover:bg-gray-50/60 dark:hover:bg-gray-900/20"
                                    >
                                        <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">
                                            {row.changed_at || "-"}
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap">
                                            <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", chip)}>
                                                {row.audit_category_label || row.audit_category}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                            {row.field_label || row.field_name || "-"}
                                        </td>
                                        <td className="px-3 py-3 text-gray-700 dark:text-gray-300 break-words max-w-[180px]">
                                            {row.old_value ?? "-"}
                                        </td>
                                        <td className="px-3 py-3 text-gray-700 dark:text-gray-300 break-words max-w-[180px]">
                                            {row.new_value ?? "-"}
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                                                <User className="h-3.5 w-3.5" />
                                                {row.changed_by_name || "Unknown"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 text-xs text-muted-foreground break-words max-w-[220px]">
                                            {notes || "-"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default EmployeeAuditContent;
